import React, { useState } from "react";
import { CalendarClock, Clock, Users, ShieldCheck, ArrowRight, Globe2, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { SLOTS, TZ, slotRange, hhmm, localTz, dayNames } from "@/lib/schedule";
import { useSchedule } from "@/lib/useSchedule";
import { AV } from "@/pages/availabilityText";
import { EventsSection } from "@/components/common/EventsSection";

function SlotPanel({ sel, onClose, x, days, slots }) {
  const navigate = useNavigate();
  if (!sel) return null;
  const entries = slots[`${sel.day}-${sel.slot}`] || [];
  const r = slotRange(sel.day, sel.slot);
  return (
    <Sheet open={!!sel} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full border-zinc-800 bg-[#0a0d18] text-zinc-100 sm:max-w-md" data-testid="slot-panel">
        <SheetHeader>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFCC00]">{days[sel.day]}</div>
          <SheetTitle className="text-left font-mono text-3xl tracking-tight text-white" data-testid="slot-panel-local">{hhmm(r.start)} – {hhmm(r.end)}</SheetTitle>
          <SheetDescription className="text-left text-[12px] text-zinc-400">
            {x.local} ({localTz()}) · <span data-testid="slot-panel-cet">{x.cet}: {hhmm(r.start, TZ)} – {hhmm(r.end, TZ)}</span>
          </SheetDescription>
        </SheetHeader>
        <div className="mt-8">
          <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500"><Users size={13} /> {x.agents}</div>
          {entries.length === 0 ? <p className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 text-[14px] text-zinc-500" data-testid="slot-panel-empty">{x.off}</p> : (
            <ul className="space-y-2.5">
              {entries.map((e, i) => (
                <li key={i} data-testid={`slot-panel-agent-${i}`} className={`flex items-start gap-3 rounded-xl border p-4 ${e.cancelled ? "border-zinc-800 bg-zinc-900/40 opacity-70" : "border-blue-500/25 bg-blue-600/[0.08]"}`}>
                  <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full text-[14px] font-bold text-white ${e.cancelled ? "bg-zinc-700" : "bg-[#003399]"}`}>{e.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}</div>
                  <div>
                    <div className={`text-[15px] font-semibold ${e.cancelled ? "text-zinc-400 line-through" : "text-zinc-50"}`}>{e.name}</div>
                    {e.cancelled && <span className="mt-1 inline-block rounded border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-300" data-testid={`slot-panel-cancelled-${i}`}>{x.cancelled}</span>}
                    {e.duty && <div className="mt-0.5 text-[13px] text-blue-200/80"><span className="text-zinc-500">{x.duty}: </span>{e.duty}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button data-testid="slot-panel-report" onClick={() => { onClose(); navigate("/"); setTimeout(() => document.getElementById("report")?.scrollIntoView({ behavior: "smooth" }), 300); }}
          className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-blue-500">
          {x.report} <ArrowRight size={15} />
        </button>
      </SheetContent>
    </Sheet>
  );
}

function NowStrip({ s, x, days }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="availability-now">
      <div className="rounded-xl border border-blue-500/30 bg-blue-600/[0.08] p-5">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300">
          <span className={`h-2 w-2 rounded-full ${s.current.length ? "animate-pulse bg-emerald-400" : "bg-zinc-600"}`} /> {x.liveNow}
        </div>
        <p className="mt-2 text-[15px] text-zinc-100">{s.current.length ? s.current.map((e) => `${e.name}${e.duty ? ` — ${e.duty}` : ""}`).join(" · ") : x.none}</p>
      </div>
      <div className="rounded-xl border border-zinc-800 bg-[#0f1220] p-5">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400"><Clock size={13} /> {x.nextShift}</div>
        <p className="mt-2 text-[15px] text-zinc-100">{s.next ? `${days[s.next.day]} ${hhmm(s.next.start)} – ${hhmm(s.next.end)}` : "—"}</p>
      </div>
    </div>
  );
}

export default function AvailabilityPage() {
  const { lang } = useLang();
  const x = AV[lang] || AV.en;
  const s = useSchedule();
  const [sel, setSel] = useState(null);
  const [duty, setDuty] = useState("");
  const days = dayNames(lang);
  const duties = [...new Set(Object.values(s.slots).flat().filter((e) => !e.cancelled && e.duty).map((e) => e.duty))].sort();

  return (
    <div className="flex min-h-screen flex-col bg-zinc-950 text-zinc-50">
      <SiteHeader />
      <section className="border-b border-zinc-800/70 bg-[#06102b]">
        <div className="mx-auto max-w-6xl px-5 py-14">
          <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#FFCC00]"><CalendarClock size={14} /> {x.kicker}</div>
          <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="availability-title">{x.title}</h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-blue-100/75">{x.intro}</p>
          <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-blue-400/20 px-3 py-1 text-[12px] text-blue-200/80"><Globe2 size={13} /> {x.tz}: {localTz()}</p>
        </div>
        <div className="h-[3px] bg-gradient-to-r from-[#003399] via-[#FFCC00] to-[#003399]" />
      </section>

      <main className="mx-auto w-full max-w-6xl flex-1 space-y-8 px-5 py-12">
        <NowStrip s={s} x={x} days={days} />
        <div className="flex items-start gap-3 rounded-xl border border-[#FFCC00]/25 bg-[#FFCC00]/[0.05] px-5 py-4 text-[13px] leading-relaxed text-zinc-300" data-testid="availability-disclaimer">
          <Info size={16} className="mt-0.5 shrink-0 text-[#FFCC00]" /> <p>{x.disclaimer}</p>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-zinc-800/70 bg-[#0b0e19] p-4" data-testid="timetable">
          {duties.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="duty-filter">
              {["", ...duties].map((d) => (
                <button key={d || "all"} data-testid={`duty-filter-${d || "all"}`} onClick={() => setDuty(d)}
                  className={`rounded-full px-3.5 py-1.5 text-[12px] font-medium transition-colors ${duty === d ? "bg-[#FFCC00] text-zinc-950" : "border border-zinc-800 text-zinc-400 hover:text-zinc-100"}`}>{d || x.filterAll}</button>
              ))}
            </div>
          )}
          <div className="grid min-w-[760px] gap-1.5" style={{ gridTemplateColumns: "96px repeat(7, minmax(0, 1fr))" }}>
            <div />
            {days.map((d, i) => (
              <div key={d} className={`pb-2 text-center text-[12px] font-semibold capitalize ${i === s.now.day ? "text-[#FFCC00]" : "text-zinc-400"}`} data-testid={`timetable-day-${i}`}>{d}</div>
            ))}
            {SLOTS.map((slot) => {
              const r = slotRange(0, slot);
              return (
                <React.Fragment key={slot}>
                  <div className="flex flex-col items-end justify-center pr-2 leading-tight" data-testid={`timetable-time-${slot}`}>
                    <span className="font-mono text-[12px] text-zinc-300">{hhmm(r.start)}</span>
                    <span className="font-mono text-[9px] text-zinc-600">CET {String(slot * 2).padStart(2, "0")}:00</span>
                  </div>
                  {days.map((_, day) => {
                    const all = s.slots[`${day}-${slot}`];
                    const live = (all || []).filter((a) => !a.cancelled);
                    const cancelled = all?.length > 0 && live.length === 0;
                    const e = live.length ? live : null;
                    const dim = duty && !(all || []).some((a) => a.duty === duty && !a.cancelled);
                    const isNow = day === s.now.day && slot === s.now.slot;
                    return (
                      <button key={day} data-testid={`slot-${day}-${slot}`} data-cancelled={cancelled ? "true" : "false"} onClick={() => setSel({ day, slot })}
                        style={cancelled ? { backgroundImage: "repeating-linear-gradient(135deg, rgba(239,68,68,0.18) 0 6px, transparent 6px 12px)" } : undefined}
                        className={`group relative h-12 rounded-md border text-left transition-all duration-200 hover:-translate-y-0.5 ${e ? "border-blue-400/40 bg-gradient-to-br from-[#1d4ed8] to-[#003399] hover:shadow-[0_6px_20px_rgba(37,99,235,0.35)]" : cancelled ? "border-red-500/30 bg-zinc-900/40" : "border-zinc-800/60 bg-zinc-900/20 hover:border-zinc-700"} ${isNow ? "ring-2 ring-[#FFCC00] ring-offset-2 ring-offset-[#0b0e19]" : ""} ${dim ? "opacity-20" : ""}`}>
                        {cancelled && <span className="absolute inset-x-2 top-1/2 -translate-y-1/2 truncate text-[10px] font-bold uppercase tracking-wider text-red-300/90">{x.cancelled}</span>}
                        {e && <span className="absolute inset-x-2 top-1.5 truncate text-[11px] font-semibold text-white">{e.map((a) => a.name).join(", ")}</span>}
                        {e && <span className="absolute inset-x-2 bottom-1.5 truncate text-[10px] text-blue-100/70">{e[0].duty}{e.length > 1 ? ` +${e.length - 1}` : ""}</span>}
                        {isNow && <span className="absolute -right-1 -top-2 rounded bg-[#FFCC00] px-1 text-[9px] font-bold uppercase text-zinc-950">{x.now}</span>}
                      </button>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-5 text-[12px] text-zinc-500">
            <span className="flex items-center gap-2"><span className="h-3 w-5 rounded bg-gradient-to-br from-[#1d4ed8] to-[#003399]" /> {x.legendOn}</span>
            <span className="flex items-center gap-2"><span className="h-3 w-5 rounded border border-zinc-800 bg-zinc-900/20" /> {x.legendOff}</span>
            <span className="flex items-center gap-2"><span className="h-3 w-5 rounded border border-red-500/30" style={{ backgroundImage: "repeating-linear-gradient(135deg, rgba(239,68,68,0.35) 0 3px, transparent 3px 6px)" }} /> {x.legendCancelled}</span>
            <span className="flex items-center gap-2"><ShieldCheck size={13} className="text-[#FFCC00]" /> {x.now}</span>
          </div>
        </div>
        <EventsSection />
      </main>
      <SlotPanel sel={sel} onClose={() => setSel(null)} x={x} days={days} slots={s.slots} />
      <SiteFooter />
    </div>
  );
}
